import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import Session from "users/__session/session";
import WorkProposalAPIRequest from "work/proposals/_api/commands/work-proposal-api-request";
import type { ProposalFirm } from "work/proposals/bidding/proposal-firm";
import Proposal from "work/proposals/proposal";
import z from "zod";

/** What became of one firm's proposal (`ProposalFirmSendResult`). */
export const ProposalFirmSendResultJSONZ = z.object({
  index: z.number().int().nonnegative(),
  leader: z.object({ userId: z.uuid(), entityId: z.uuid() }),
  outcome: z.enum(["Sent", "NotSent"]),
  /**
   * The request every firm's copy of this send answers. The comparison of the send opens by it
   * (#6457). The server derives it from a hash, so it is a GUID but not an RFC 9562 UUID.
   */
  rfpId: z.guid(),
  proposalId: z.uuid().nullish(),
  error: z.string().nullish(),
});
export type ProposalFirmSendResultJSON = z.infer<typeof ProposalFirmSendResultJSONZ>;

export type SendProposalToFirmsArgs = {
  proposal: Proposal;
  firms: readonly ProposalFirm[];
  session: Session;
  /** Minted once per send and resent unchanged on a retry, so no firm is drafted twice. */
  commandId: string;
};

/**
 * The body of `POST work/proposals/firm-sends`: the proposal as a create command, without the
 * vendor team the server replaces per firm, and the firms in the client's order.
 */
export function sendProposalToFirmsBody({ proposal, firms, session, commandId }: SendProposalToFirmsArgs) {
  const shared = { ...new WorkProposalAPIRequest(proposal, session, undefined, commandId).payload };
  // Each firm's team comes from `firms`, and the server chooses the request id the copies share.
  delete shared.vendorTeam;
  delete shared.RFPId;
  return {
    commandId,
    proposal: shared,
    firms: firms.map((firm) => ({
      leader: { userId: firm.leader.userId.value, entityId: firm.leader.legalEntityId.value },
      members: (firm.members ?? []).map((member) => ({
        userId: member.userId.value,
        entityId: member.legalEntityId.value,
      })),
      marketplaceId: firm.marketplaceId?.value,
    })),
  };
}

/**
 * The terms a send gives every firm: the shared proposal of its body, as text, without the command
 * id. Two sends with the same terms are a retry of one another; different terms are a new send.
 */
export function proposalFirmSendTerms(proposal: Proposal, session: Session): string {
  return JSON.stringify(sendProposalToFirmsBody({ proposal, firms: [], session, commandId: "" }).proposal);
}

export const proposalFirmSendApi = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Sends one proposal to several firms (#576). Answers 200 when every firm received it and 207
     * when any did not; both carry a result per firm, and RTK Query treats both as success.
     */
    sendProposalToFirms: build.mutation<ProposalFirmSendResultJSON[], SendProposalToFirmsArgs>({
      query: (args) => ({
        url: "work/proposals/firm-sends",
        method: "POST",
        body: sendProposalToFirmsBody(args),
      }),
      rawResponseSchema: z.array(ProposalFirmSendResultJSONZ),
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
      ],
    }),
  }),
});

export const { useSendProposalToFirmsMutation } = proposalFirmSendApi;
